import { useEffect, useRef, useState } from 'react';
import { Trash2, Plus } from 'lucide-react';

interface Note {
  id: string;
  text: string;
  createdAt: number;
}

function storageKey(candidateId: string) {
  return `ggj.candidate.notes.v1.${candidateId}`;
}

function loadNotes(candidateId: string): Note[] {
  try {
    const raw = localStorage.getItem(storageKey(candidateId));
    if (!raw) return [];
    return JSON.parse(raw) as Note[];
  } catch {
    return [];
  }
}

function saveNotes(candidateId: string, notes: Note[]) {
  try {
    localStorage.setItem(storageKey(candidateId), JSON.stringify(notes));
  } catch {
    // localStorage unavailable
  }
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(ts).toLocaleDateString();
}

interface Props {
  candidateId: string;
}

export function CandidateNotesTab({ candidateId }: Props) {
  const [notes, setNotes] = useState<Note[]>(() => loadNotes(candidateId));
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reload when candidateId changes
  useEffect(() => {
    setNotes(loadNotes(candidateId));
    setDraft('');
  }, [candidateId]);

  const addNote = () => {
    const text = draft.trim();
    if (!text) return;
    const updated = [
      { id: crypto.randomUUID(), text, createdAt: Date.now() },
      ...notes,
    ];
    setNotes(updated);
    saveNotes(candidateId, updated);
    setDraft('');
    textareaRef.current?.focus();
  };

  const deleteNote = (id: string) => {
    const updated = notes.filter((n) => n.id !== id);
    setNotes(updated);
    saveNotes(candidateId, updated);
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="rounded-[10px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)]">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              addNote();
            }
          }}
          placeholder="Add a note about this candidate…"
          rows={3}
          className="w-full resize-none bg-transparent px-3 pt-3 text-[13px] vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none"
        />
        <div className="flex items-center justify-between border-t border-[var(--vscode-border-primary)] px-3 py-2">
          <span className="text-[11px] vscode-text-tertiary">⌘↵ to save</span>
          <button
            type="button"
            disabled={!draft.trim()}
            onClick={addNote}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> Add note
          </button>
        </div>
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <p className="py-6 text-center text-[13px] vscode-text-tertiary">
          No notes yet. Add your first note above.
        </p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="group relative rounded-[10px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] p-3"
            >
              <p className="whitespace-pre-wrap text-[13px] vscode-text-primary leading-relaxed pr-6">
                {note.text}
              </p>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] vscode-text-tertiary">{formatRelative(note.createdAt)}</span>
                <button
                  type="button"
                  onClick={() => deleteNote(note.id)}
                  className="rounded p-1 text-red-400 opacity-0 transition-opacity hover:bg-red-500/10 group-hover:opacity-100"
                  aria-label="Delete note"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TODO(§8-v2): server-side persistence */}
    </div>
  );
}
